import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react'

import { apiZodios } from '~api/apiZodios'
import outlookZodios from '~api/outlookApiZodios'
import { type CasePdfOptions, generateCasePdfBlob } from '~api/services/cases/casePdf'
import { type GetIdentityViewRequestData, type PreviewVoiceRequest } from '~types'

export interface CasePdf {
  blob: Blob
  url: string
}

export const reduxApi = createApi({
  baseQuery: fakeBaseQuery(),
  endpoints: (build) => ({
    generateResponseStream: build.mutation({
      queryFn: async (args: Parameters<typeof apiZodios.ai_generateResponse>[0]) => {
        try {
          const isOutlook = typeof window !== 'undefined' && window.Office !== undefined
          const generateFn = isOutlook
            ? outlookZodios.generateResponse
            : apiZodios.ai_generateResponse
          const data = await generateFn(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    generateAssistantResponseStream: build.mutation({
      queryFn: async (args: Parameters<typeof apiZodios.ai_generateAssistantResponse>[0]) => {
        try {
          const data = await apiZodios.ai_generateAssistantResponse(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getIdentity: build.query({
      queryFn: async (args: GetIdentityViewRequestData) => {
        try {
          const data = await apiZodios.core_getIdentity(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listStaffers: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.core_listStaffers>[0]) => {
        try {
          const data = await apiZodios.core_listStaffers(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    searchContacts: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.contacts_searchContacts>[0]) => {
        try {
          const data = await apiZodios.contacts_searchContacts(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listIdentitiesByIds: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.core_listIdentitiesByIds>[0]) => {
        try {
          const data = await apiZodios.core_listIdentitiesByIds(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listTags: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.core_listTags>[0]) => {
        try {
          const data = await apiZodios.core_listTags(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listLicenseTemplates: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.licenses_listLicenseTemplates>[0]) => {
        try {
          const data = await apiZodios.licenses_listLicenseTemplates(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listBaseProcesses: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_listBaseProcesses>[0]) => {
        try {
          const data = await apiZodios.cases_listBaseProcesses(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listInputs: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_listInputs>[0]) => {
        try {
          const data = await apiZodios.cases_listInputs(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listSections: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_listSections>[0]) => {
        try {
          const data = await apiZodios.cases_listSections(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listForms: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_listForms>[0]) => {
        try {
          const data = await apiZodios.cases_listForms(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listProcessCases: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_listProcessCases>[0]) => {
        try {
          const data = await apiZodios.cases_listProcessCases(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listExpressions: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_listExpressions>[0]) => {
        try {
          const data = await apiZodios.cases_listExpressions(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listValues: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_listValues>[0]) => {
        try {
          const data = await apiZodios.cases_listValues(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listCalculatedInputs: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_listCalculatedInputs>[0]) => {
        try {
          const data = await apiZodios.cases_listCalculatedInputs(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listAIInteractionSummaries: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.ai_listAIInteractionSummaries>[0]) => {
        try {
          const data = await apiZodios.ai_listAIInteractionSummaries(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getOrganizationAIInteractionStats: build.query({
      queryFn: async (
        args: Parameters<typeof apiZodios.analytics_getOrganizationAIInteractionStats>[0],
      ) => {
        try {
          const data = await apiZodios.analytics_getOrganizationAIInteractionStats(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getOrganizationAIInteractionFlows: build.query({
      queryFn: async (
        args: Parameters<typeof apiZodios.analytics_getOrganizationAIInteractionFlows>[0],
      ) => {
        try {
          const data = await apiZodios.analytics_getOrganizationAIInteractionFlows(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getCustomerPurchasedStatuses: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.core_getCustomerPurchasedStatuses>[0]) => {
        try {
          const data = await apiZodios.core_getCustomerPurchasedStatuses(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    previewVoice: build.mutation<string, PreviewVoiceRequest>({
      queryFn: async (args) => {
        try {
          const response = await apiZodios.axios.post<Blob>('/ai/preview-voice/', args, {
            responseType: 'blob',
          })
          return { data: URL.createObjectURL(response.data) }
        } catch (error) {
          return { error }
        }
      },
    }),
    getOrganizationAISettings: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.ai_initializeAI>[0]) => {
        try {
          const data = await apiZodios.ai_initializeAI(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getEvaluationCategories: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.ai_getEvaluationCategories>[0]) => {
        try {
          const data = await apiZodios.ai_getEvaluationCategories(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    createCustomCategoryEvaluationConfig: build.mutation({
      queryFn: async (
        args: Parameters<typeof apiZodios.ai_createCustomCategoryEvaluationConfig>[0],
      ) => {
        try {
          const data = await apiZodios.ai_createCustomCategoryEvaluationConfig(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    updateCustomCategoryEvaluationConfig: build.mutation({
      queryFn: async (
        args: Parameters<typeof apiZodios.ai_updateCustomCategoryEvaluationConfig>[0],
      ) => {
        try {
          const data = await apiZodios.ai_updateCustomCategoryEvaluationConfig(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getInteractionCategoryUpdateStatus: build.query({
      queryFn: async (
        args: Parameters<typeof apiZodios.ai_getInteractionCategoryUpdateStatus>[0],
      ) => {
        try {
          const data = await apiZodios.ai_getInteractionCategoryUpdateStatus(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    updateInteractionCategories: build.mutation({
      queryFn: async (args: Parameters<typeof apiZodios.ai_updateInteractionCategories>[0]) => {
        try {
          const data = await apiZodios.ai_updateInteractionCategories(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    checkCategoryEvaluationConfig: build.mutation({
      queryFn: async (args: Parameters<typeof apiZodios.ai_checkCategoryEvaluationConfig>[0]) => {
        try {
          const data = await apiZodios.ai_checkCategoryEvaluationConfig(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    createVoicemailProcess: build.mutation({
      queryFn: async (args: Parameters<typeof apiZodios.cases_createVoicemailProcess>[0]) => {
        try {
          const data = await apiZodios.cases_createVoicemailProcess(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getProcess: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_getProcess>[0]) => {
        try {
          const data = await apiZodios.cases_getProcess(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getCaseFeedDocuments: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_getCaseFeedDocuments>[0]) => {
        try {
          const data = await apiZodios.cases_getCaseFeedDocuments(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getCaseFeedInteractions: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_getCaseFeedInteractions>[0]) => {
        try {
          const data = await apiZodios.cases_getCaseFeedInteractions(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getFrontDeskData: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.analytics_getFrontDeskData>[0]) => {
        try {
          const data = await apiZodios.analytics_getFrontDeskData(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getKPIs: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.analytics_getKPIs>[0]) => {
        try {
          const data = await apiZodios.analytics_getKPIs(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getIntegrations: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.analytics_getIntegrations>[0]) => {
        try {
          const data = await apiZodios.analytics_getIntegrations(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getHealthCheckSummary: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.analytics_getHealthCheckSummary>[0]) => {
        try {
          const data = await apiZodios.analytics_getHealthCheckSummary(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getKnowledgeBaseQuality: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.analytics_getKnowledgeBaseQuality>[0]) => {
        try {
          const data = await apiZodios.analytics_getKnowledgeBaseQuality(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getChannelVolume: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.analytics_getChannelVolume>[0]) => {
        try {
          const data = await apiZodios.analytics_getChannelVolume(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getTopDomains: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.analytics_getTopDomains>[0]) => {
        try {
          const data = await apiZodios.analytics_getTopDomains(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getTopCategories: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.analytics_getTopCategories>[0]) => {
        try {
          const data = await apiZodios.analytics_getTopCategories(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getWorkflowPerformance: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.analytics_getWorkflowPerformance>[0]) => {
        try {
          const data = await apiZodios.analytics_getWorkflowPerformance(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getTasks: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.analytics_getTasks>[0]) => {
        try {
          const data = await apiZodios.analytics_getTasks(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getCaseFeedInfo: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_getCaseFeedInfo>[0]) => {
        try {
          const data = await apiZodios.cases_getCaseFeedInfo(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getCaseFeedContactRecordInfo: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_getCaseFeedContactRecordInfo>[0]) => {
        try {
          const data = await apiZodios.cases_getCaseFeedContactRecordInfo(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getCaseFeedParties: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_getCaseFeedParties>[0]) => {
        try {
          const data = await apiZodios.cases_getCaseFeedParties(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getCaseFeedContactRecordParties: build.query({
      queryFn: async (
        args: Parameters<typeof apiZodios.cases_getCaseFeedContactRecordParties>[0],
      ) => {
        try {
          const data = await apiZodios.cases_getCaseFeedContactRecordParties(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    generateAICaseSummary: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_generateAICaseSummary>[0]) => {
        try {
          const data = await apiZodios.cases_generateAICaseSummary(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    generateAIEmailSummary: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_generateAIEmailSummary>[0]) => {
        try {
          const data = await apiZodios.cases_generateAIEmailSummary(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    generateAIContactSummary: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.contacts_generateAIContactSummary>[0]) => {
        try {
          const data = await apiZodios.contacts_generateAIContactSummary(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    requestAnalyticsReport: build.mutation({
      queryFn: async (args: Parameters<typeof apiZodios.analytics_requestAIAnalyticsReport>[0]) => {
        try {
          const data = await apiZodios.analytics_requestAIAnalyticsReport(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getCaseFeedActivity: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_getCaseFeedActivity>[0]) => {
        try {
          const data = await apiZodios.cases_getCaseFeedActivity(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    getCaseFeedWorkflow: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_getCaseFeedWorkflow>[0]) => {
        try {
          const data = await apiZodios.cases_getCaseFeedWorkflow(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listTasks: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_listTasks>[0]) => {
        try {
          const data = await apiZodios.cases_listTasks(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    setResponseFeedbackImpression: build.mutation({
      queryFn: async (args: Parameters<typeof apiZodios.ai_setResponseFeedbackImpression>[0]) => {
        try {
          const data = await apiZodios.ai_setResponseFeedbackImpression(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    submitResponseFeedback: build.mutation({
      queryFn: async (args: Parameters<typeof apiZodios.ai_submitResponseFeedback>[0]) => {
        try {
          const data = await apiZodios.ai_submitResponseFeedback(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listEmailAddresses: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.contacts_listEmailAddresses>[0]) => {
        try {
          const data = await apiZodios.contacts_listEmailAddresses(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listAIEmails: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.ai_listAIEmails>[0]) => {
        try {
          const data = await apiZodios.ai_listAIEmails(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    upsertAIEmail: build.mutation({
      queryFn: async (args: Parameters<typeof apiZodios.ai_upsertAIEmail>[0]) => {
        try {
          const data = await apiZodios.ai_upsertAIEmail(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listTemplates: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_listTemplates>[0]) => {
        try {
          const data = await apiZodios.cases_listTemplates(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    listCaseExportFiles: build.query({
      queryFn: async (args: Parameters<typeof apiZodios.cases_listCaseExportFiles>[0]) => {
        try {
          const data = await apiZodios.cases_listCaseExportFiles(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
    generateCasePdf: build.query<CasePdf, CasePdfOptions>({
      keepUnusedDataFor: 60,
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${JSON.stringify({
          userId: queryArgs.userId,
          caseId: queryArgs.caseId,
          stepIds: [...queryArgs.stepIds].sort(),
          includeEmails: queryArgs.includeEmails,
          includeLogs: queryArgs.includeLogs,
          includeTasks: queryArgs.includeTasks,
          includeDescription: queryArgs.includeDescription,
          includeCaseDetails: queryArgs.includeCaseDetails,
          selectedInputsByStep: Object.entries(queryArgs.selectedInputsByStep ?? {})
            .map(([formStepId, inputStepIds]) => [formStepId, [...inputStepIds].sort()])
            .sort(([leftFormStepId], [rightFormStepId]) =>
              String(leftFormStepId).localeCompare(String(rightFormStepId)),
            ),
          writeTiff: queryArgs.writeTiff ?? false,
        })})`,
      queryFn: async (options) => {
        try {
          const blob = await generateCasePdfBlob(options)
          return { data: { blob, url: URL.createObjectURL(blob) } }
        } catch (error) {
          return { error }
        }
      },
      onCacheEntryAdded: async (_options, { cacheDataLoaded, cacheEntryRemoved }) => {
        try {
          const { data } = await cacheDataLoaded
          await cacheEntryRemoved
          URL.revokeObjectURL(data.url)
        } catch {
          await cacheEntryRemoved
        }
      },
    }),
    deleteAIEmail: build.mutation({
      queryFn: async (args: Parameters<typeof apiZodios.ai_deleteAIEmail>[0]) => {
        try {
          const data = await apiZodios.ai_deleteAIEmail(args)
          return { data }
        } catch (error) {
          return { error }
        }
      },
    }),
  }),
})

export const {
  useGenerateResponseStreamMutation,
  useGenerateAssistantResponseStreamMutation,
  useGetIdentityQuery,
  useListStaffersQuery,
  useSearchContactsQuery,
  useListIdentitiesByIdsQuery,
  useListTagsQuery,
  useListLicenseTemplatesQuery,
  useListBaseProcessesQuery,
  useListInputsQuery,
  useListSectionsQuery,
  useListFormsQuery,
  useListProcessCasesQuery,
  useListExpressionsQuery,
  useListValuesQuery,
  useListCalculatedInputsQuery,
  useListAIInteractionSummariesQuery,
  useGetOrganizationAIInteractionStatsQuery,
  useGetOrganizationAIInteractionFlowsQuery,
  useGetCustomerPurchasedStatusesQuery,
  usePreviewVoiceMutation,
  useGetOrganizationAISettingsQuery,
  useGetEvaluationCategoriesQuery,
  useCreateCustomCategoryEvaluationConfigMutation,
  useUpdateCustomCategoryEvaluationConfigMutation,
  useGetInteractionCategoryUpdateStatusQuery,
  useUpdateInteractionCategoriesMutation,
  useCheckCategoryEvaluationConfigMutation,
  useCreateVoicemailProcessMutation,
  useGetProcessQuery,
  useGetCaseFeedDocumentsQuery,
  useGetCaseFeedInteractionsQuery,
  useGetFrontDeskDataQuery,
  useGetKPIsQuery,
  useGetIntegrationsQuery,
  useGetTopDomainsQuery,
  useGetHealthCheckSummaryQuery,
  useGetKnowledgeBaseQualityQuery,
  useGetChannelVolumeQuery,
  useGetTopCategoriesQuery,
  useGetWorkflowPerformanceQuery,
  useGetTasksQuery,
  useGetCaseFeedInfoQuery,
  useGetCaseFeedContactRecordInfoQuery,
  useGetCaseFeedPartiesQuery,
  useGetCaseFeedContactRecordPartiesQuery,
  useGenerateAICaseSummaryQuery,
  useGenerateAIEmailSummaryQuery,
  useGenerateAIContactSummaryQuery,
  useRequestAnalyticsReportMutation,
  useGetCaseFeedActivityQuery,
  useGetCaseFeedWorkflowQuery,
  useListTasksQuery,
  useSetResponseFeedbackImpressionMutation,
  useSubmitResponseFeedbackMutation,
  useListEmailAddressesQuery,
  useListAIEmailsQuery,
  useUpsertAIEmailMutation,
  useDeleteAIEmailMutation,
  useListTemplatesQuery,
  useGenerateCasePdfQuery,
  useListCaseExportFilesQuery,
} = reduxApi
